<script setup lang="ts">
import { LogOut, Settings } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ROLE_CONFIG } from '@/config/role-config';
import { useAuthStore } from '@/stores/auth.store';
import { useSidebar } from '@composables/useSidebar';

const { t } = useI18n();
const router = useRouter();
const { isCollapsed } = useSidebar();
const authStore = useAuthStore();

const userName = computed(() => authStore.user?.fullName ?? '');

const roleLabel = computed(() => {
  const role = authStore.user?.role;

  if (role && role in ROLE_CONFIG) {
    return t(ROLE_CONFIG[role].labelKey);
  }

  return authStore.user?.username ?? '';
});

const initials = computed(() => {
  const name = userName.value;

  return name.charAt(0).toUpperCase();
});

function handleLogout(): void {
  void authStore.logout();
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        class="hover:bg-accent flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors"
        :class="isCollapsed ? 'justify-center' : ''"
      >
        <span class="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
          <span class="text-muted-foreground text-sm font-medium">{{ initials }}</span>
        </span>

        <span v-if="!isCollapsed" class="flex min-w-0 flex-col text-left">
          <span class="text-foreground truncate text-sm font-medium">{{ userName }}</span>
          <span class="text-muted-foreground truncate text-xs">{{ roleLabel }}</span>
        </span>
      </button>
    </DropdownMenuTrigger>

    <DropdownMenuContent side="top" align="start" class="w-56">
      <DropdownMenuLabel class="font-normal">
        <div class="flex flex-col gap-1">
          <p class="text-sm leading-none font-medium">{{ userName }}</p>
          <p class="text-muted-foreground text-xs leading-none">{{ roleLabel }}</p>
        </div>
      </DropdownMenuLabel>

      <DropdownMenuSeparator />

      <DropdownMenuItem @select="router.push('/settings')">
        <Settings />
        {{ t('sidebar.settings') }}
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem variant="destructive" @select="handleLogout">
        <LogOut />
        {{ t('auth.logout') }}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
